/**
 * Minimal path matcher over the route registry, with the same rules the
 * client router (wouter + regexparam) applies: case-insensitive, optional
 * trailing slash, ":param" segments. Used to preload a route's code before
 * the app mounts and by build scripts; tests/client keep it in sync with wouter.
 */

import { activeRoutes, routePaths, type RouteDef } from "./routes";

export interface RouteMatch {
  route: RouteDef;
  /** The registry path or alias that matched. */
  pattern: string;
  params: Record<string, string>;
}

/**
 * Pattern handed to the client router. regexparam already accepts an optional
 * trailing slash, so registry paths like "/blog/" are registered without it.
 */
export function routerPattern(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

/** Like wouter's unescape: a malformed escape keeps the raw segment. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function segments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

export function matchPattern(
  pattern: string,
  pathname: string
): Record<string, string> | null {
  const patternParts = segments(pattern);
  const pathParts = segments(pathname);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const expected = patternParts[i];
    const actual = pathParts[i];
    if (expected.startsWith(":")) {
      params[expected.slice(1)] = safeDecode(actual);
    } else if (expected.toLowerCase() !== actual.toLowerCase()) {
      return null;
    }
  }
  return params;
}

export function findRoute(
  pathname: string,
  includeDrafts = false
): RouteMatch | null {
  for (const route of activeRoutes(includeDrafts)) {
    for (const pattern of routePaths(route)) {
      const params = matchPattern(pattern, pathname);
      if (params) return { route, pattern, params };
    }
  }
  return null;
}
